import { useRef, useEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { useGame, MODES, WORLDS, TRACKS } from "./store";
import {
  trackCurve,
  trackLength,
  stations,
  zones,
  getTrackFrame,
  lookaheadCurve,
  hazards,
  hazardMinRadius,
  scenarioGates,
} from "./track";
import * as Sound from "./sound";

function useKeyboard() {
  const keys = useRef({});
  useEffect(() => {
    const down = (e) => {
      keys.current[e.code] = true;
    };
    const up = (e) => {
      keys.current[e.code] = false;
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);
  return keys;
}

const WATER_Y = -1.0;
const GROUND_Y = -2.0;
const EYE_HEIGHT = 1.6; // first-person camera offset above feet

export default function GameLoop() {
  const keys = useKeyboard();
  const camera = useThree((st) => st.camera);

  // Refs (mutable physics state, not React state — avoids re-renders)
  const sRef = useRef(0);
  const prevSRef = useRef(0);
  const segRef = useRef("A");
  const vRef = useRef(5);
  const velRef = useRef(new THREE.Vector3());
  const posRef = useRef(new THREE.Vector3());
  const groundYawRef = useRef(0); // facing direction on ground / water (radians)
  const stationsHitRef = useRef(new Set());
  const decisionsHitRef = useRef(new Set());
  const lastJumpRef = useRef(false);
  const lastParaRef = useRef(false);
  const prevModeRef = useRef(null);
  // Drive-by-Physics hazard tracking
  const inHazardRef = useRef(null);       // hazard currently inside (or null)
  const hazardMaxVRef = useRef(0);        // max speed reached inside current hazard
  const overGripTimerRef = useRef(0);     // seconds continuously over the grip limit
  const hazardCacheRef = useRef({});      // hazardId → { rMin, slope } (computed once)
  const landingRef = useRef(false);       // true once final-station braking begins
  const metricsRef = useRef({ energy: 0, wear: 0, risk: 0, lastFlush: 0 }); // challenge metrics
  // Phase 2 (SUVAT & Energy) event state
  // Scenario gate timing — 30s warmup, 30s minimum spacing
  const elapsedRef = useRef(0);           // total ON_TRACK seconds this run
  const lastGateTimeRef = useRef(-9999);  // elapsed seconds when last gate fired

  useFrame((_, dtRaw) => {
    const dtBase = Math.min(dtRaw, 0.05);
    const state = useGame.getState();
    // Slow-motion during consequence preview (5× slower)
    const dt = state.slowMotion ? dtBase * 0.2 : dtBase;
    const { mode, world, track, mass, parachuteOpen, parachuteArea } = state;
    const W = WORLDS[world];
    const T = TRACKS[track];
    if (!W || !T) return;
    const g = W.g;
    const rhoAir = W.rhoAir * (T.airMult ?? 1.0);  // track can modulate air density
    const rhoWater = W.rhoWater;

    // ----------- Mode-change side effects (sound) -----------
    if (mode !== prevModeRef.current) {
      if (prevModeRef.current === MODES.AIRBORNE && mode === MODES.IN_WATER) {
        Sound.playSplash();
      }
      // On fresh start (MENU/FINISHED → ON_TRACK), reset session-scoped tracking
      if (
        (prevModeRef.current === MODES.MENU || prevModeRef.current === MODES.FINISHED) &&
        mode === MODES.ON_TRACK
      ) {
        decisionsHitRef.current = new Set();
        stationsHitRef.current = new Set();
        sRef.current = 0;
        prevSRef.current = 0;
        segRef.current = "A";
        vRef.current = 5;
        inHazardRef.current = null;
        hazardMaxVRef.current = 0;
        overGripTimerRef.current = 0;
        landingRef.current = false;
        metricsRef.current = { energy: 0, wear: 0, risk: 0, lastFlush: 0 };
        elapsedRef.current = 0;
        lastGateTimeRef.current = -9999;
      }
      // Mode-exclusive sounds
      Sound.setEngine(mode === MODES.ON_TRACK);
      Sound.setRailRumble(mode === MODES.ON_TRACK);
      Sound.setWind(mode === MODES.AIRBORNE);
      Sound.setSwim(mode === MODES.IN_WATER);
      // Birds & breathing managed per-frame below
      if (mode !== MODES.ON_GROUND) {
        Sound.setBirds(false);
        Sound.setBreathing(false);
      }
      prevModeRef.current = mode;
    }

    // ====================== ON_TRACK ======================
    if (mode === MODES.ON_TRACK) {
      // ----- RESPAWN sync (after a crash retry) -----
      if (state.respawnS !== null) {
        sRef.current = state.respawnS;
        prevSRef.current = state.respawnS;
        vRef.current = state.respawnV ?? 4;
        const f = getTrackFrame(trackCurve, sRef.current);
        posRef.current = f.point.clone();
        posRef.current.y += 0.7;
        velRef.current = f.tangent.clone().multiplyScalar(vRef.current);
        inHazardRef.current = null;
        hazardMaxVRef.current = 0;
        overGripTimerRef.current = 0;
        useGame.setState({ respawnS: null });
      }

      // One-shot speed override: "watch what happens at YOUR speed"
      if (state.forceSpeed != null) {
        vRef.current = state.forceSpeed;
        useGame.setState({ forceSpeed: null });
      }

      // Accumulate on-track elapsed time (not counting pauses)
      if (!state.paused) {
        elapsedRef.current += dtBase;
      }

      // Determine current zone based on progress (s)
      const currentS = Math.min(sRef.current, 0.9999);
      let newZone = zones[0].id;
      for (const z of zones) {
        if (currentS >= z.startS && currentS < z.endS) {
          newZone = z.id;
          break;
        }
      }
      if (newZone !== state.currentZone) {
        useGame.setState({ currentZone: newZone });
      }

      // Global timer updates if cart is moving
      if (vRef.current > 0.1) {
        useGame.setState((s) => ({ globalTimer: s.globalTimer + dt }));
      }

      // If in Zone 8 (Distance Plain), run stopwatch
      if (newZone === "ZONE_8" && vRef.current > 0.1) {
        if (!state.stopwatchActive) useGame.setState({ stopwatchActive: true });
        useGame.setState((s) => ({ stopwatchTime: s.stopwatchTime + dt }));
      } else if (state.stopwatchActive && newZone !== "ZONE_8") {
        useGame.setState({ stopwatchActive: false });
      }

      // ----- DECISION PAUSE check -----
      // While paused, the cart is fully frozen.
      if (state.paused) {
        const { tangent, slope } = getTrackFrame(trackCurve, sRef.current);
        const patch = {
          s: sRef.current,
          v: vRef.current,
          a: 0,
          N: mass * g * Math.cos(slope),
          pos: posRef.current.toArray(),
          vel: tangent.clone().multiplyScalar(vRef.current).toArray(),
          height: posRef.current.y - WATER_Y,
        };
        // For hazard decisions, upcomingRadius/Slope were set at trigger time
        // to the hazard's binding (minimum) radius — don't overwrite them.
        if (!state.decision?.hazardId) {
          const look = lookaheadCurve(trackCurve, sRef.current, 0.12);
          patch.upcomingRadius = look.radius;
          patch.upcomingSlope = look.slope;
        }
        useGame.setState(patch);
        Sound.setEngine(false);
        Sound.setRailRumble(false);
        return;
      }

      const { point, tangent, radius, slope } = getTrackFrame(
        trackCurve,
        sRef.current,
      );
      const look = lookaheadCurve(trackCurve, sRef.current, 0.12);

      // ---- Physics-Console-driven speed control ----
      const accelKey = keys.current["ArrowUp"];
      const brakeKey = keys.current["ArrowDown"];
      
      let engineAccel = 0;
      if (accelKey) engineAccel = 8.0;   // 8 m/s² forward thrust
      if (brakeKey) engineAccel = -12.0; // 12 m/s² brake

      // In CHALLENGE mode the friction slider takes over from the track preset
      const challenge = !!state.challengeGoal;
      const muEff = challenge ? state.challengeMu : T.mu;
      // Extra grip term: higher μ raises curve limits (and wear!) in challenge
      const muGrip = challenge ? state.challengeMu : 0;

      const sNow = Math.min(sRef.current, 0.9999);

      const friction = muEff * g * Math.cos(slope);
      const gComponent = -g * Math.sin(slope);
      const cartArea = 2.0;
      const Cd = 0.8;
      const airDragAccel =
        (0.5 * rhoAir * Cd * cartArea * vRef.current * vRef.current) / mass;

      let aAlong = engineAccel + gComponent;
      aAlong -= friction * Math.sign(vRef.current || 1);
      aAlong -= airDragAccel * Math.sign(vRef.current || 1);

      // ----- SMOOTH STATION LANDING (final approach) -----
      // From s = 0.965, the brakes take over and glide the cart to a gentle
      // stop at the home station instead of an abrupt cut-off.
      const sForLanding = Math.min(sRef.current, 0.9999);
      if (sForLanding >= 0.965 && !state.paused) {
        if (!landingRef.current) {
          landingRef.current = true;
          useGame.setState({
            lastHazardResult: {
              outcome: "safe",
              msg: "🏁 Final approach — auto-brakes easing you into the station…",
              t: Date.now(),
            },
            hazardHUD: null,
          });
        }
        const dRem = Math.max(0.5, (0.998 - sForLanding) * trackLength);
        if (vRef.current > 1.5) {
          // Decelerate just enough to arrive at walking pace
          aAlong = -Math.min(10, (vRef.current * vRef.current) / (2 * dRem));
        } else {
          aAlong = 0; // coast the last metres at ~1.5 m/s
        }
        // Creep to a stop right at the platform
        if (sForLanding >= 0.994) {
          vRef.current = Math.max(0, vRef.current - 2.0 * dt);
          aAlong = 0;
          if (vRef.current <= 0.05) {
            Sound.setEngine(false);
            Sound.setRailRumble(false);
            Sound.setSiren(false);
            Sound.playChime();
            useGame.setState({ mode: MODES.FINISHED, v: 0, a: 0 });
            return;
          }
        }
      }

      // Track analytics
      if (vRef.current > state.analytics.maxSpeed) {
        useGame.setState((s) => ({
          analytics: { ...s.analytics, maxSpeed: vRef.current }
        }));
      }

      // ═══════════ SCENARIO GATES (creative physics events) ═══════════
      // Rules:
      //   1. Only in normal mode (not challenge, not Phase 2)
      //   2. At least 30 s of on-track time must have elapsed before the FIRST gate
      //   3. At least 30 s must have passed since the LAST gate fired
      //   4. The cart must have passed the gate's s-checkpoint (position filter)
      // This guarantees questions are separated by roughly 30s of gameplay.
      if (!challenge) {
        const MIN_WARMUP   = 30; // seconds before first gate can fire
        const MIN_GAP      = 30; // seconds minimum between successive gates
        const elapsed      = elapsedRef.current;
        const sinceLastGate = elapsed - lastGateTimeRef.current;

        if (elapsed >= MIN_WARMUP && sinceLastGate >= MIN_GAP) {
          for (const gate of scenarioGates) {
            if (decisionsHitRef.current.has(gate.id)) continue;
            if (sNow >= gate.checkpointS) {
              decisionsHitRef.current.add(gate.id);
              lastGateTimeRef.current = elapsed;
              vRef.current = Math.min(vRef.current, 5); // ease to near-stop
              useGame.setState({
                paused: true,
                decision: {
                  kind: "scenario",
                  label: gate.label,
                  scenarioId: gate.id,
                  scenario: gate.scenario,
                },
                decisionAttempts: 0,
                savedS: gate.checkpointS,
                savedV: 4,
              });
              return;
            }
          }
        }
      }
      // ═══════════ end scenario gates ═══════════

      // ═══════════ DRIVE-BY-PHYSICS: hazard system ═══════════

      // Cache hazard geometry once
      for (const hz of hazards) {
        if (!hazardCacheRef.current[hz.id]) {
          hazardCacheRef.current[hz.id] = hazardMinRadius(hz);
        }
      }

      // (1) CHECKPOINT — first encounter pauses for the POE question
      // Same 30s warmup / 30s gap rule as scenario gates applies here too.
      if (!challenge) {
        const MIN_WARMUP    = 30;
        const MIN_GAP       = 30;
        const elapsed2      = elapsedRef.current;
        const sinceLastGate2 = elapsed2 - lastGateTimeRef.current;

        if (elapsed2 >= MIN_WARMUP && sinceLastGate2 >= MIN_GAP) {
          for (const hz of hazards) {
            if (decisionsHitRef.current.has(hz.id)) continue;
            if (prevSRef.current < hz.checkpointS && sNow >= hz.checkpointS) {
              decisionsHitRef.current.add(hz.id);
              lastGateTimeRef.current = elapsed2;
              const geom = hazardCacheRef.current[hz.id];
              const cp = getTrackFrame(trackCurve, hz.checkpointS);
              vRef.current = Math.min(vRef.current, 6);
              Sound.playChime();
              useGame.setState({
                paused: true,
                decision: {
                  kind: "curve",
                  label: `⚠ ${hz.label} ahead — find the safe speed!`,
                  hazardId: hz.id,
                },
                decisionAttempts: 0,
                upcomingRadius: geom.rMin,
                upcomingSlope: geom.slope,
                savedPos: cp.point.toArray(),
                savedS: hz.checkpointS,
                savedV: 4,
              });
              return;
            }
          }
        } else {
          // Time gate not met yet — silently mark the hazard as passed so it
          // doesn't queue up and fire all at once later when the gate opens.
          // The cart still gets the grip physics (derail risk) but no question.
          for (const hz of hazards) {
            if (decisionsHitRef.current.has(hz.id)) continue;
            if (prevSRef.current < hz.checkpointS && sNow >= hz.checkpointS) {
              decisionsHitRef.current.add(hz.id); // skip question, mark visited
            }
          }
        }
      }

      // (2) IN-HAZARD grip physics — exceed v_max and you derail
      let activeHazard = null;
      for (const hz of hazards) {
        if (sNow >= hz.sStart && sNow < hz.sEnd) { activeHazard = hz; break; }
      }

      if (activeHazard) {
        if (inHazardRef.current !== activeHazard.id) {
          inHazardRef.current = activeHazard.id;
          hazardMaxVRef.current = 0;
          overGripTimerRef.current = 0;
        }
        hazardMaxVRef.current = Math.max(hazardMaxVRef.current, vRef.current);

        const rLocal = Math.max(5, Math.min(radius, 9999));
        const vmaxLocal = Math.sqrt(g * rLocal * (Math.cos(slope) + muGrip));
        if (rLocal < 300 && vRef.current > vmaxLocal * 1.08) {
          overGripTimerRef.current += dtBase;
        } else {
          overGripTimerRef.current = Math.max(0, overGripTimerRef.current - dtBase * 2);
        }

        // DERAIL: grip exceeded for ~0.35 s — physics wins
        if (overGripTimerRef.current > 0.35) {
          const geom = hazardCacheRef.current[activeHazard.id];
          const vmaxHaz = Math.sqrt(g * geom.rMin * (Math.cos(geom.slope) + muGrip));
          // Fly off along the tangent with an outward + upward component
          const t2v = trackCurve.getTangentAt((sNow + 0.01) % 1).normalize();
          const turn = new THREE.Vector3().crossVectors(tangent, t2v).y;
          const { right } = getTrackFrame(trackCurve, sNow);
          const outward = right.clone().multiplyScalar(Math.sign(turn || 1) * vRef.current * 0.45);
          velRef.current = tangent.clone().multiplyScalar(vRef.current)
            .add(outward)
            .add(new THREE.Vector3(0, 3.5, 0));
          Sound.setEngine(false);
          Sound.setRailRumble(false);
          Sound.setSiren(false);
          useGame.setState((s) => ({
            mode: MODES.AIRBORNE,
            consequencePreview: true,
            slowMotion: true,
            crashes: s.crashes + 1,
            savedDecision: {
              kind: "curve",
              label: activeHazard.label,
              hazardId: activeHazard.id,
              fromDriving: true,
              rMin: geom.rMin,
              slope: geom.slope,
            },
            savedSpeed: vRef.current,
            savedCorrectAnswer: vmaxHaz,
            savedS: activeHazard.checkpointS,
            savedV: 4,
            hazardResults: [
              ...s.hazardResults.filter((h) => h.id !== activeHazard.id),
              {
                id: activeHazard.id,
                label: activeHazard.label,
                maxV: vRef.current,
                vmax: vmaxHaz,
                outcome: "crashed",
                points: 0,
              },
            ],
            lastHazardResult: {
              outcome: "crashed",
              msg: `💥 DERAILED! v²/r needed more force than the track could give.`,
              t: Date.now(),
            },
          }));
          inHazardRef.current = null;
          return;
        }
      } else if (inHazardRef.current) {
        // (3) EXITED a hazard cleanly — award precision points
        const hz = hazards.find((h) => h.id === inHazardRef.current);
        inHazardRef.current = null;
        if (hz) {
          const geom = hazardCacheRef.current[hz.id];
          const vmaxHaz = Math.sqrt(g * geom.rMin * (Math.cos(geom.slope) + muGrip));
          const ratio = hazardMaxVRef.current / vmaxHaz;
          let pts, outcome, msg;
          if (ratio > 1.0) {
            pts = 5; outcome = "risky";
            msg = `⚠ OVER THE LIMIT (${(ratio * 100).toFixed(0)}% of v_max) — you got lucky this time! +5`;
          } else if (ratio >= 0.85) {
            pts = 25; outcome = "perfect";
            msg = `🏆 PERFECT PHYSICS! ${(ratio * 100).toFixed(0)}% of v_max — fast AND safe. +25`;
          } else if (ratio >= 0.55) {
            pts = 12; outcome = "safe";
            msg = `✓ Cleared at ${(ratio * 100).toFixed(0)}% of v_max. Push closer to the limit! +12`;
          } else {
            pts = 5; outcome = "cautious";
            msg = `🐢 Very cautious (${(ratio * 100).toFixed(0)}% of v_max). Trust your calculation! +5`;
          }
          useGame.setState((s) => ({
            points: s.points + pts,
            hazardResults: [
              ...s.hazardResults.filter((h) => h.id !== hz.id),
              { id: hz.id, label: hz.label, maxV: hazardMaxVRef.current, vmax: vmaxHaz, outcome, points: pts },
            ],
            lastHazardResult: { outcome, msg, t: Date.now() },
            // Clear any pending consequence flags (e.g. student braked out
            // of a wrong-answer test and survived — that's also learning!)
            consequencePreview: false,
            savedDecision: null,
          }));
          Sound.playChime();
        }
      }

      // (4) Live HUD data for the nearest hazard
      {
        let next = null;
        let bestDist = Infinity;
        for (const hz of hazards) {
          let dS = hz.sStart - sNow;
          if (dS < 0) dS += 1;
          const inside = sNow >= hz.sStart && sNow < hz.sEnd;
          const d = inside ? 0 : dS * trackLength;
          if (d < bestDist) { bestDist = d; next = { hz, inside, distM: d }; }
        }
        if (next && bestDist < 200) {
          const geom = hazardCacheRef.current[next.hz.id];
          const gripAccel = g * geom.rMin * (Math.cos(geom.slope) + muGrip);
          const vmaxHaz = Math.sqrt(gripAccel);
          const grip = (vRef.current * vRef.current) / gripAccel;
          useGame.setState({
            hazardHUD: {
              label: next.hz.label,
              distM: next.distM,
              rMin: geom.rMin,
              // In Challenge mode limits are always visible (fluency/review)
              vmax: challenge || state.hazardVmax[next.hz.id] != null ? vmaxHaz : null,
              inside: next.inside,
              grip,
              v: vRef.current,
            },
          });
        } else if (state.hazardHUD) {
          useGame.setState({ hazardHUD: null });
        }
      }
      // ═══════════ end hazard system ═══════════


      vRef.current += aAlong * dt;
      // Allow coasting backward briefly on steep hills (v can go slightly negative)
      vRef.current = Math.max(-3, vRef.current);
      prevSRef.current = sRef.current;
      // Clamp s to [0, 1) — track is open, not a loop
      sRef.current = Math.max(0, Math.min(0.9999, sRef.current + (vRef.current * dt) / trackLength));

      Sound.setEngine(true, vRef.current);
      Sound.setRailRumble(true, vRef.current);

      // Grip siren — warn when approaching the physical limit in a hazard
      Sound.setSiren(!!(activeHazard && state.hazardHUD && state.hazardHUD.grip > 0.8));

      const dt2 = 0.01;
      const tanNext = trackCurve
        .getTangentAt(Math.min(1, sRef.current + dt2))
        .normalize();
      const dY = tanNext.y - tangent.y;
      const sign = dY >= 0 ? 1 : -1;
      const N =
        mass * g * Math.cos(slope) +
        sign * (mass * vRef.current * vRef.current) / Math.max(radius, 1);

      // ----- CHALLENGE METRICS (energy / wear / risk) -----
      if (challenge) {
        const m = metricsRef.current;
        if (engineAccel > 0) m.energy += mass * engineAccel * vRef.current * dt; // engine work (J)
        m.wear += muEff * Math.abs(N) * vRef.current * dt;                       // friction work (J)
        if (activeHazard && state.hazardHUD && state.hazardHUD.grip > 0.9) m.risk += dtBase;
        if (state.globalTimer - m.lastFlush > 0.5) {
          m.lastFlush = state.globalTimer;
          useGame.setState((s) => ({
            challengeMetrics: {
              energy: m.energy,
              wear: m.wear,
              riskTime: m.risk,
              crashes: s.crashes,
            },
            // Friction sparks: high μ at speed grinds the rails
            sparksActive: muEff > 0.35 && vRef.current > 4,
          }));
        }
      }

      posRef.current = point.clone();
      posRef.current.y += 0.7;
      velRef.current.copy(tangent.clone().multiplyScalar(vRef.current));

      // Station crossings
      for (let i = 0; i < stations.length; i++) {
        const st = stations[i];
        if (stationsHitRef.current.has(i)) continue;
        if (Math.abs(sRef.current - st.s) < 0.012) {
          stationsHitRef.current.add(i);
          Sound.playChime();
          useGame.setState((s) => ({
            stationsCrossed: s.stationsCrossed + 1,
            points: s.points + 10,
            coins: s.coins + 5,
          }));
        }
      }

      if (sRef.current >= 0.9999) {
        Sound.setEngine(false);
        Sound.setRailRumble(false);
        Sound.playChime();
        useGame.setState({ mode: MODES.FINISHED });
        return;
      }

      useGame.setState({
        s: sRef.current,
        v: vRef.current,
        a: aAlong,
        N,
        pos: posRef.current.toArray(),
        vel: velRef.current.toArray(),
        height: posRef.current.y - WATER_Y,
        upcomingRadius: look.radius,
        upcomingSlope: look.slope,
        debug_s: sRef.current,
        debug_decHits: decisionsHitRef.current.size,
        activeEquations: [
          "F_net = m·a = " + (mass * aAlong).toFixed(0) + " N",
          "N = m·g·cosθ ± m·v²/r = " + N.toFixed(0) + " N",
          "F_friction = μ·N = " +
            (T.mu * Math.abs(N)).toFixed(0) +
            " N (μ=" +
            T.mu +
            ")",
          "θ = " + ((slope * 180) / Math.PI).toFixed(1) + "° , r = " +
            (radius > 999 ? "∞" : radius.toFixed(1)) + " m",
        ],
      });
    }

    // ====================== AIRBORNE ======================
    else if (mode === MODES.AIRBORNE) {
      const v = velRef.current;
      const speed = v.length();
      const A = state.parachuteArea;
      const Cd = state.parachuteCd;
      let CdA = A > 0.1 ? Cd * A : 0.8 * 2.0;
      const dragMag = 0.5 * rhoAir * CdA * speed * speed;
      const dragAccelMag = dragMag / mass;
      const dragVec = speed > 0.01 ? v.clone().normalize().multiplyScalar(-dragAccelMag) : new THREE.Vector3();
      const acc = new THREE.Vector3(0, -g, 0).add(dragVec);
      velRef.current.add(acc.clone().multiplyScalar(dt));
      posRef.current.add(velRef.current.clone().multiplyScalar(dt));

      Sound.setWind(true, speed);

      // Water landing
      if (posRef.current.y <= WATER_Y) {
        const vy = Math.abs(velRef.current.y);
        const tax = Math.min(40, Math.round(vy * 0.8));
        useGame.setState((s) => ({
          mode: MODES.IN_WATER,
          coins: Math.max(0, s.coins - (state.parachuteOpen ? 0 : tax)),
          points: s.points + 10,
          // Crash flight over — exit slow-mo so the Explain modal can appear
          slowMotion: s.consequencePreview ? false : s.slowMotion,
        }));
        posRef.current.y = WATER_Y;
        velRef.current.set(0, 0, 0);
        groundYawRef.current = Math.atan2(v.x, v.z);
        return;
      }

      // Ground landing
      if (posRef.current.y <= GROUND_Y) {
        posRef.current.y = GROUND_Y;
        velRef.current.y = 0;
        useGame.setState((s) => ({
          mode: MODES.ON_GROUND,
          // Crash flight over — exit slow-mo so the Explain modal can appear
          slowMotion: s.consequencePreview ? false : s.slowMotion,
        }));
        return;
      }

      useGame.setState({
        pos: posRef.current.toArray(),
        vel: velRef.current.toArray(),
        v: speed,
        a: acc.length(),
        N: 0,
        height: posRef.current.y - WATER_Y,
        activeEquations: [
          `x(t) = x₀ + vₓ·t,  y(t) = y₀ + v_y·t − ½g·t²`,
          `g = ${g} m/s²,  v = ${speed.toFixed(1)} m/s`,
        ],
      });
    }

    // ====================== IN_WATER ======================
    else if (mode === MODES.IN_WATER) {
      const upthrust = keys.current["ArrowUp"] || keys.current["Space"] ? 1 : 0;
      const swimFwd = keys.current["KeyW"] ? 1 : 0;
      const swimBack = keys.current["KeyS"] ? 1 : 0;
      const turnL = keys.current["KeyA"] || keys.current["ArrowLeft"] ? 1 : 0;
      const turnR = keys.current["KeyD"] || keys.current["ArrowRight"] ? 1 : 0;
      const exit = keys.current["KeyR"];

      // C = climb back onto the track at the nearest station
      // (blocked while the Explain modal is open — explain first!)
      if (keys.current["KeyC"] && !state.consequencePreview) {
        let bestI = 0;
        let bestD = Infinity;
        for (let i = 0; i < stations.length; i++) {
          const p = trackCurve.getPointAt(stations[i].s);
          const dx = posRef.current.x - p.x;
          const dz = posRef.current.z - p.z;
          const d = dx * dx + dz * dz;
          if (d < bestD) { bestD = d; bestI = i; }
        }
        const st = stations[bestI];
        const tp = trackCurve.getPointAt(st.s);
        posRef.current.set(tp.x, tp.y + 0.7, tp.z);
        sRef.current = st.s;
        prevSRef.current = st.s;
        vRef.current = 3;
        velRef.current.set(0, 0, 0);
        Sound.playClimb();
        useGame.setState({ mode: MODES.ON_TRACK });
        return;
      }

      if (turnL) groundYawRef.current += 2.0 * dt;
      if (turnR) groundYawRef.current -= 2.0 * dt;
      const fwd = new THREE.Vector3(Math.sin(groundYawRef.current), 0, Math.cos(groundYawRef.current));

      const V = state.displacedVolume;
      const rho = state.waterDensity;
      const Fb = rho * V * g;
      const Fg = mass * g;
      const v = velRef.current;
      const speed = v.length();
      const waterDragCoeff = 60;
      const dragVec = speed > 0.01 ? v.clone().normalize().multiplyScalar(-(waterDragCoeff * speed) / mass) : new THREE.Vector3();
      const aY = (Fb - Fg) / mass;
      const acc = new THREE.Vector3(0, aY, 0).add(dragVec);
      if (upthrust) acc.y += 5.0;
      if (swimFwd) acc.add(fwd.clone().multiplyScalar(3.5));
      if (swimBack) acc.add(fwd.clone().multiplyScalar(-2.5));

      velRef.current.add(acc.clone().multiplyScalar(dt));
      posRef.current.add(velRef.current.clone().multiplyScalar(dt));

      if (posRef.current.y > WATER_Y + 0.4) {
        posRef.current.y = WATER_Y + 0.4;
        if (velRef.current.y > 0) velRef.current.y = 0;
      }
      if (posRef.current.y < WATER_Y - 8) {
        posRef.current.y = WATER_Y - 8;
        velRef.current.y = 0;
      }

      if (exit) {
        useGame.setState((s) => ({
          mode: MODES.ON_GROUND,
          points: s.points + 20,
          coins: s.coins + 5,
        }));
        posRef.current.z = -125;
        posRef.current.y = GROUND_Y + EYE_HEIGHT - 0.1;
        velRef.current.set(0, 0, 0);
      }

      useGame.setState({
        pos: posRef.current.toArray(),
        vel: velRef.current.toArray(),
        v: speed,
        a: acc.length(),
        N: 0,
        activeEquations: [
          `You're in the water! 🌊`,
          `🪜 Press C — climb back onto the track (nearest station)`,
          `W: swim · A/D: turn · ↑ or Space: float up · R: swim to shore`,
        ],
      });
    }

    // ====================== ON_GROUND ======================
    else if (mode === MODES.ON_GROUND) {
      const fwd = keys.current["KeyW"] || keys.current["ArrowUp"];
      const back = keys.current["KeyS"] || keys.current["ArrowDown"];
      const turnL = keys.current["KeyA"] || keys.current["ArrowLeft"];
      const turnR = keys.current["KeyD"] || keys.current["ArrowRight"];
      const running = keys.current["ShiftLeft"] || keys.current["ShiftRight"];
      const climb = keys.current["KeyC"];
      const exitKey = keys.current["KeyR"];

      if (turnL) groundYawRef.current += 2.2 * dt;
      if (turnR) groundYawRef.current -= 2.2 * dt;

      const forward = new THREE.Vector3(
        Math.sin(groundYawRef.current),
        0,
        Math.cos(groundYawRef.current),
      );
      // SLIDER-DRIVEN: walkSpeed slider sets speed. Shift gives a 1.6× boost.
      const baseSpeed = state.walkSpeed;
      const speed = running ? baseSpeed * 1.6 : baseSpeed;
      const isMoving = fwd || back;
      const moveDir = new THREE.Vector3();
      if (fwd) moveDir.add(forward);
      if (back) moveDir.sub(forward);
      if (moveDir.lengthSq() > 0) {
        moveDir.normalize().multiplyScalar(speed * dt);
        posRef.current.add(moveDir);
        Sound.playFootstep(running);
      }
      posRef.current.y = GROUND_Y + EYE_HEIGHT - 0.1;

      // State-aware ambient sounds: birds when idle, breathing when running
      Sound.setBirds(!isMoving);
      Sound.setBreathing(isMoving && running);

      // Detect nearest station ladder (within 4m horizontal distance to ladder base)
      let nearestStation = null;
      let nearestDist = Infinity;
      for (let i = 0; i < stations.length; i++) {
        const st = stations[i];
        const p = trackCurve.getPointAt(st.s);
        // ladder base = (p.x + 1.6, GROUND, p.z)
        const dx = posRef.current.x - (p.x + 1.6);
        const dz = posRef.current.z - p.z;
        const d = Math.sqrt(dx * dx + dz * dz);
        if (d < nearestDist) {
          nearestDist = d;
          nearestStation = { index: i, ...st, point: p };
        }
      }
      const canClimb = true; // C works anywhere — always returns to nearest station

      // Climb action — teleport back to the track at the nearest station
      // (blocked while the Explain modal is open — explain first!)
      if (climb && nearestStation && !state.consequencePreview) {
        const tp = nearestStation.point;
        posRef.current.set(tp.x, tp.y + 0.7, tp.z);
        sRef.current = nearestStation.s;
        prevSRef.current = nearestStation.s;
        vRef.current = 3;
        velRef.current.set(0, 0, 0);
        useGame.setState({
          mode: MODES.ON_TRACK,
          points: state.points + 5,
        });
        Sound.playClimb();
        return;
      }

      // R key still triggers recap if there is one
      if (exitKey) {
        if (state.recap) {
          useGame.setState({ mode: MODES.RECAP });
        }
      }

      useGame.setState({
        pos: posRef.current.toArray(),
        vel: forward
          .clone()
          .multiplyScalar(fwd ? speed : back ? -speed : 0)
          .toArray(),
        v: isMoving ? speed : 0,
        a: 0,
        N: mass * g,
        nearestStation: canClimb ? nearestStation : null,
        activeEquations: [
          isMoving
            ? running
              ? `RUNNING — v = ${speed.toFixed(1)} m/s · breathing fast`
              : `WALKING — v = ${speed.toFixed(1)} m/s`
            : `Standing still — listen to the birds 🐦`,
          `🪜 Press C to climb back onto the track (${nearestStation ? nearestStation.label : "nearest station"})`,
          state.recap ? `R to open recap & resume on track` : `W/S walk · A/D turn · Shift run faster`,
        ],
      });
    }

    // ----------- Camera -----------
    if (posRef.current && camera) {
      let camPos, lookTarget;

      if (mode === MODES.ON_GROUND || mode === MODES.IN_WATER) {
        // First-person walking/swimming
        camPos = posRef.current.clone();
        camPos.y += 0.4;
        const lookDir = new THREE.Vector3(
          Math.sin(groundYawRef.current),
          0,
          Math.cos(groundYawRef.current),
        );
        camera.position.lerp(camPos, 0.4);
        lookTarget = camPos.clone().add(lookDir.multiplyScalar(10));
        camera.lookAt(lookTarget);
      } else if (mode === MODES.ON_TRACK) {
        // Chase-cam: behind and above the cart, looking ahead along the track
        const { tangent, up } = getTrackFrame(trackCurve, sRef.current);
        const cartPos = posRef.current.clone();
        // Camera 14m behind, 7m above the cart
        const behind = tangent.clone().multiplyScalar(-14);
        const above  = up.clone().multiplyScalar(7);
        camPos = cartPos.clone().add(behind).add(above);
        // Look at a point 18m ahead of the cart (so you see what's coming)
        const aheadPt = cartPos.clone().add(tangent.clone().multiplyScalar(18));
        camera.position.lerp(camPos, 0.09);
        camera.lookAt(aheadPt);
      } else {
        // Airborne / other — projectile chase
        camPos = posRef.current.clone().add(new THREE.Vector3(0, 3, 12));
        camera.position.lerp(camPos, 0.15);
        const target = posRef.current.clone();
        if (mode === MODES.AIRBORNE) target.y -= 1;
        camera.lookAt(target);
      }
    }
  });

  return null;
}
